import type { ColumnsType } from 'antd/es/table';
import { useCallback, useMemo } from 'react';

import Typo from '@/components/Typography/Typo';
import {
  CHART_FIELD,
  CHART_TOTAL_ROW_KEY,
  CHART_X_FIELD_NAME,
} from '@/entities/chart/model/constants';
import useChartLabel from '@/features/(charts)/chart-renderer/hooks/use-chart-label';
import chartUtils from '@/features/(charts)/chart-renderer/lib/chart-utils';
import useTagStore from '@/stores/TagStore';
import { getTagParentsArr } from '@/utils/tag';

type Props = {
  chart: ChartBase;
  tableData: ChartTableRes;
};

const useChartTableMeta = (props: Props) => {
  const { chart, tableData } = props;
  const getLabel = useChartLabel(tableData.field_item_meta);
  const tagMap = useTagStore((state) => state.tagMap);

  const makeLevelColumns = useCallback(
    (maxLevel: number) => {
      const columns: ColumnsType<any> = [];
      Array.from({ length: maxLevel }, (_, index) => {
        columns.push({
          title: `Level ${index + 1}`,
          dataIndex: `level ${index + 1}`,
          key: `level ${index + 1}`,
          fixed: 'left',
          width: 100,
          onCell: (record: any) => {
            if (record.key === CHART_TOTAL_ROW_KEY) {
              if (index === 0) {
                return {
                  colSpan: maxLevel,
                };
              }
              return {
                colSpan: 0,
              };
            }
            return {};
          },
          render: (text: string, record: any) => {
            if (record.key === CHART_TOTAL_ROW_KEY) {
              if (index === 0) {
                return <Typo weight="sb">Total</Typo>;
              }
              return '';
            }
            return text ? getLabel(chart.field.type, text, true) : text;
          },
        });
      });
      return columns;
    },
    [chart.field.type, getLabel],
  );

  const crossSortedData = chartUtils.getSortedRowItems(
    chart,
    tableData,
    chart.field.candidates || [],
  );

  const items = crossSortedData.tables.reduce((acc, value) => {
    return [...acc, ...value.map((x) => x.x)];
  }, [] as StringNumberValue[]);

  const baseColumns = useMemo(() => {
    const baseColumns: ColumnsType<any> = [];
    switch (chart.field.type) {
      case CHART_FIELD.TAXONOMY_TOPIC: {
        const meta = tableData.field_item_meta || [];

        const maxLevel = items.reduce((acc: number, item) => {
          const curr = meta?.find((m) => m.alias === item);
          if (!curr) return acc;
          const level = curr.level;
          return level < acc ? acc : level;
        }, 0);

        const levelColumns = makeLevelColumns(maxLevel);
        baseColumns.push(...levelColumns);
        break;
      }
      case CHART_FIELD.FEEDBACK_TAG: {
        const maxLevel = items.reduce((acc: number, item) => {
          const curr = tagMap[item];
          if (!curr) return acc;
          const level = curr.level;
          return level < acc ? acc : level;
        }, 0);

        const levelColumns = makeLevelColumns(maxLevel);
        baseColumns.push(...levelColumns);
        break;
      }
      default: {
        baseColumns.push({
          title: 'Item',
          dataIndex: CHART_X_FIELD_NAME,
          key: CHART_X_FIELD_NAME,
          fixed: 'left',
          width: 150,
          render: (text: string, record: any) => {
            if (record.key === CHART_TOTAL_ROW_KEY) {
              return <Typo weight="sb">Total</Typo>;
            }
            return getLabel(chart.field.type, text);
          },
        });
        break;
      }
    }

    return baseColumns;
  }, [
    chart.field.type,
    getLabel,
    items,
    makeLevelColumns,
    tableData.field_item_meta,
    tagMap,
  ]);

  const getLevelRowMap = useCallback(
    (item: StringNumberValue) => {
      const levelRowMap: Record<string, StringNumberValue> = {};
      switch (chart.field.type) {
        case CHART_FIELD.FEEDBACK_TAG: {
          const tag = tagMap[item];
          if (!tag) return {};
          const parents = getTagParentsArr(tagMap, item as string);

          parents.forEach((parent) => {
            levelRowMap[`level ${parent.level}`] = parent.alias;
          });
          levelRowMap[`level ${tag.level}`] = tag.alias;
          break;
        }
        case CHART_FIELD.TAXONOMY_TOPIC: {
          const meta = tableData.field_item_meta || [];
          const topic = meta.find((m) => m.alias === item);
          topic?.parent_topic_lv3 &&
            (levelRowMap[`level 3`] = topic.parent_topic_lv3.parent_id);
          topic?.parent_topic_lv2 &&
            (levelRowMap[`level 2`] = topic.parent_topic_lv2.parent_id);
          topic?.parent_topic_lv1 &&
            (levelRowMap[`level 1`] = topic.parent_topic_lv1.parent_id);
          topic?.level &&
            (levelRowMap[`level ${topic.level}`] = topic.alias as string);
          break;
        }
        default: {
          levelRowMap[CHART_X_FIELD_NAME] = item;
          break;
        }
      }
      return levelRowMap;
    },
    [chart.field.type, tableData.field_item_meta, tagMap],
  );

  const getTotalRowMap = useCallback(() => {
    const levelRowMap: Record<string, StringNumberValue> = {};
    switch (chart.field.type) {
      case CHART_FIELD.FEEDBACK_TAG:
      case CHART_FIELD.TAXONOMY_TOPIC: {
        levelRowMap[`level 1`] = 'Total';
        break;
      }
      default: {
        levelRowMap[CHART_X_FIELD_NAME] = 'Total';
        break;
      }
    }
    return levelRowMap;
  }, [chart.field.type]);

  return {
    baseColumns,
    getLevelRowMap,
    getTotalRowMap,
  };
};

export default useChartTableMeta;
